import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { EditParcelItemTypeDto, ParcelItemTypesApi, ParcelItemTypesInListDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-edit-parcel-item-type',
  templateUrl: './edit-parcel-item-type.component.html',
  styleUrls: ['./edit-parcel-item-type.component.css'],
  imports: [
    NzModalComponent,
    NzModalTitleDirective,
    NzModalContentDirective,
    ErrorDisplayComponent,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzInputDirective,
    NzFormLabelComponent,
    NzFormControlComponent,
  ],
  standalone: true,
})
export class EditParcelItemTypeComponent implements OnChanges {
  private formBuilder = inject(FormBuilder);
  private parcelItemTypesApi = inject(ParcelItemTypesApi);
  private notification = inject(NzNotificationService);
  private queryClient = injectQueryClient();

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  form = this.formBuilder.group({
    category: ['', [Validators.required]],
    maxWeight: [0, [Validators.min(0)]],
    maxVolume: [0, [Validators.min(0)]],
    description: ['', [Validators.required]],
    defaultCost: [10000, [Validators.required, Validators.min(0)]],
  });

  @Input()
  show = false;

  @Input()
  parcelItemType: ParcelItemTypesInListDto | null = null;

  @Output()
  hide = new EventEmitter(true);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parcelItemType'] && this.parcelItemType) {
      this.form.patchValue({
        category: this.parcelItemType.category,
        maxWeight: this.parcelItemType.maxWeight ?? 0,
        maxVolume: this.parcelItemType.maxVolume ?? 0,
        description: this.parcelItemType.description,
        defaultCost: this.parcelItemType.defaultCost ?? 10000,
      });
    }
  }

  // Tanstack Query mutation for editing parcel item type
  editMutation = injectMutation(() => ({
    mutationFn: (dto: EditParcelItemTypeDto) =>
      lastValueFrom(this.parcelItemTypesApi.parcelItemTypesControllerUpdate({ id: dto.id, body: dto })),
    onSuccess: () => {
      // Invalidate and refetch parcel item types queries
      this.queryClient.invalidateQueries({ queryKey: ['parcel-item-types'] });

      this.clearErrors();
      this.show = false;
      this.closeDrawer();
      this.form.reset();
      this.notification.success('✅ Success', 'Parcel item type has been updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating parcel item type:', error);
      const result = this.formErrorHandler.handleFormError(error, { formName: 'tenant-edit-parcel-item-type-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));

  closeDrawer() {
    this.hide.emit(true);
    this.show = false;
    this.form.reset();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  edit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.parcelItemType || !this.parcelItemType.id) {
      this.notification.error('❌ Error', 'Parcel item type ID is missing');
      return;
    }

    this.clearErrors();

    const payload: EditParcelItemTypeDto = {
      id: this.parcelItemType.id,
      category: this.form.get('category')!.value!,
      maxWeight: Number(this.form.get('maxWeight')!.value!),
      maxVolume: Number(this.form.get('maxVolume')!.value!),
      description: this.form.get('description')!.value!,
      defaultCost: Number(this.form.get('defaultCost')!.value!),
    };

    this.editMutation.mutate(payload);
  }
}
